import { useEffect, useState } from 'react';
import {
  ShieldCheck,
  BadgeCheck,
  CreditCard,
  Headphones,
  X,
  Search,
  Wallet,
} from 'lucide-react';
import { useSectionTranslation } from '@/hooks';
import { trustStripTranslations } from './translations';

const items = [
  { key: 'escrow', icon: ShieldCheck },
  { key: 'verified', icon: BadgeCheck },
  { key: 'payments', icon: CreditCard },
  { key: 'support', icon: Headphones },
] as const;

const stepIcons = [Search, Wallet, ShieldCheck];

export function TrustStrip() {
  const t = useSectionTranslation(trustStripTranslations);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <section className="py-10 border-y border-surface-border bg-surface-card/30" aria-label={t.ariaLabel}>
      <div className="container-main">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
            {items.map(({ key, icon: Icon }) => (
              <div key={key} className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface-card border border-surface-border text-accent">
                  <Icon size={18} strokeWidth={1.5} />
                </div>
                <span className="text-sm font-medium text-brand-white">{t.items[key].label}</span>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="btn-secondary h-10 shrink-0 self-start lg:self-center"
          >
            {t.learnMore}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="escrow-modal-title"
        >
          <button
            type="button"
            aria-label={t.modal.close}
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm cursor-default"
          />
          <div className="relative w-full max-w-lg rounded-2xl bg-surface-card border border-surface-border p-6 md:p-8 shadow-card">
            <div className="flex items-start justify-between gap-4 mb-6">
              <h3 id="escrow-modal-title" className="font-display text-2xl text-brand-white">
                {t.modal.title}
              </h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t.modal.close}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-surface-border text-brand-muted hover:text-brand-white hover:border-accent/30 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <ol className="space-y-5">
              {t.modal.steps.map((step, index) => {
                const Icon = stepIcons[index] ?? ShieldCheck;
                return (
                  <li key={step.number} className="flex gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface-hover border border-surface-border text-accent">
                      <Icon size={18} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-brand-white">
                        <span className="text-accent me-2">{step.number}</span>
                        {step.title}
                      </p>
                      <p className="text-sm text-brand-muted mt-1 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      )}
    </section>
  );
}
